import * as AccordionPrimitive from '@radix-ui/react-accordion';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
} from '@folio/components';
import { DocumentViewer, useDocumentViewer } from '@folio/components/features/document-viewer';
import { DocumentsCollection } from '@folio/components/module-elements/Documents/DocumentsCollection';
import { Tooltip, TooltipContent, TooltipTrigger } from '@folio/components/ui/tooltip';
import { useQuery } from '@tanstack/react-query';
import {
  configControllerGetPublicProductConfigQueryOptions,
  keyValueFieldsToObject,
  legacyPermissionsControllerAtMeQueryOptions,
  viewSpecificControllerListProductCommonDocumentsQueryOptions,
} from 'korint-sdk';
import {
  type KorintModulesProductsConfig,
  CustomFunnelAuth,
  useFeatureFlag,
  useKorintProductConfig,
} from 'korint-sdk/react';
import { sanitize } from '@folio/utils';
import { useToast } from '@folio/components';
import { ChevronDown, Check, Copy, Globe, SearchIcon } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';
import { ThemeIcon } from '@folio/components/ui/theme-icon';

function getAnonymousFunnelPath(
  config: KorintModulesProductsConfig | undefined,
  brokerageFirmId: string,
): string | undefined {
  const anonymousFunnel = config?.customFunnels?.find(f => f.auth === CustomFunnelAuth.ANONYMOUS);
  if (!anonymousFunnel) return undefined;
  const embeddedBasePath = anonymousFunnel.basePath.replace('/anonymous/', '/anonymous-embedded/');
  const landingPath = embeddedBasePath.split('/:customerId')[0];
  return landingPath.replace(':brokerageFirmId', brokerageFirmId);
}

function getAnonymousStandaloneFunnelPath(
  config: KorintModulesProductsConfig | undefined,
  brokerageFirmId: string,
): string | undefined {
  const anonymousFunnel = config?.customFunnels?.find(
    f => f.auth === CustomFunnelAuth.ANONYMOUS && f.customLayout,
  );
  if (!anonymousFunnel) return undefined;
  const landingPath = anonymousFunnel.basePath.split('/:customerId')[0];
  return landingPath.replace(':brokerageFirmId', brokerageFirmId);
}

export function DocumentsCommonListProductModule() {
  const { t } = useTranslation();
  const { productsConfig } = useKorintProductConfig();

  const { data: user, status: getUserStatus } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const [search, setSearch] = useState('');

  const productIds = user?.productIds?.filter(id => id in productsConfig) ?? [];

  const normalizedSearch = sanitize(search);

  const visibleProductIds = useMemo(() => {
    const filtered = productIds.filter(id => {
      if (!normalizedSearch) return true;
      const label = sanitize(t(`products.${id}.label`));
      return label.includes(normalizedSearch);
    });
    return filtered.sort((a, b) => {
      const labelA = t(`products.${a}.label`);
      const labelB = t(`products.${b}.label`);
      return labelA.localeCompare(labelB, 'fr');
    });
  }, [productIds, normalizedSearch, t]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('products.commonDocumentsTitle')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {getUserStatus === 'pending' && (
          <Stack gap={2}>
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </Stack>
        )}

        {getUserStatus === 'error' && (
          <div className="text-center text-muted-foreground py-4">
            {t('products.productsLoadError')}
          </div>
        )}

        {getUserStatus === 'success' && (
          <>
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <input
                type="text"
                placeholder={t('products.searchPlaceholder', 'Rechercher un produit...')}
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background pl-10 pr-4 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>

            <Accordion type="multiple" className="flex flex-col gap-2">
              {visibleProductIds.map(productId => (
                <ProductDocumentsSection
                  key={productId}
                  productId={productId}
                  icon={
                    productsConfig[productId]?.display.icon as React.ComponentType<{
                      className?: string;
                    }>
                  }
                  productConfig={productsConfig[productId]}
                  brokerageFirmId={user?.brokerageFirmId}
                />
              ))}
              {visibleProductIds.length === 0 && (
                <div className="text-center text-muted-foreground py-4 text-sm">
                  {t('products.noProductMatch')}
                </div>
              )}
            </Accordion>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ProductDocumentsSection({
  productId,
  icon: Icon,
  productConfig,
  brokerageFirmId,
}: {
  productId: string;
  icon: React.ComponentType<{ className?: string }>;
  productConfig: KorintModulesProductsConfig | undefined;
  brokerageFirmId: string | undefined;
}) {
  const { t } = useTranslation();
  const label = t(`products.${productId}.label`);

  const anonymousFunnelPath = brokerageFirmId
    ? getAnonymousFunnelPath(productConfig, brokerageFirmId)
    : undefined;
  const anonymousStandaloneFunnelPath = brokerageFirmId
    ? getAnonymousStandaloneFunnelPath(productConfig, brokerageFirmId)
    : undefined;

  return (
    <AccordionItem value={productId} display="inline">
      <AccordionPrimitive.Header className="flex items-center">
        <AccordionPrimitive.Trigger className="peer flex flex-1 items-center gap-3 p-3 transition-colors hover:bg-muted/50 text-left">
          <ThemeIcon size="md">
            <Icon />
          </ThemeIcon>
          <Text inline size="sm" weight="medium">
            {label}
          </Text>
        </AccordionPrimitive.Trigger>
        <Group gap={1} className="pr-3">
          {(anonymousFunnelPath || anonymousStandaloneFunnelPath) && (
            <AnonymousFunnelShareLinks
              productId={productId}
              anonymousFunnelPath={anonymousFunnelPath}
              anonymousStandaloneFunnelPath={anonymousStandaloneFunnelPath}
            />
          )}
          <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 peer-data-[state=open]:rotate-180" />
        </Group>
      </AccordionPrimitive.Header>
      <AccordionContent display="inline">
        <ProductDocumentsContent productId={productId} />
      </AccordionContent>
    </AccordionItem>
  );
}

/**
 * Share links are gated per product, not per tenant: an intermediary can be allowed
 * to share one product's anonymous funnel without exposing the others. Mounted only
 * for products that actually have an anonymous funnel, so the flag is evaluated once
 * per such product instead of once per listed product.
 */
function AnonymousFunnelShareLinks({
  productId,
  anonymousFunnelPath,
  anonymousStandaloneFunnelPath,
}: {
  productId: string;
  anonymousFunnelPath?: string;
  anonymousStandaloneFunnelPath?: string;
}) {
  const shareLinksEnabled = useFeatureFlag('feat_anonymous_funnel_links', productId);

  if (!shareLinksEnabled) return null;

  return (
    <>
      {anonymousFunnelPath && (
        <CopyFunnelButton url={`${window.location.origin}${anonymousFunnelPath}`} />
      )}
      {anonymousStandaloneFunnelPath && (
        <StandaloneFunnelLinkButton
          url={`${window.location.origin}${anonymousStandaloneFunnelPath}`}
        />
      )}
    </>
  );
}

function CopyFunnelButton({ url }: { url: string }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const handleClick = () => {
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      toast({ title: t('products.anonymousLinkCopied') });
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={t('products.copyAnonymousLink')}
          className="mr-1 size-7 flex items-center justify-center rounded-full bg-muted text-muted-foreground hover:text-foreground active:scale-90 transition-all duration-150 cursor-pointer"
          onClick={handleClick}
        >
          {copied ? <Check className="size-3.5 text-green-500" /> : <Copy className="size-3.5" />}
        </button>
      </TooltipTrigger>
      <TooltipContent>{t('products.copyAnonymousLink')}</TooltipContent>
    </Tooltip>
  );
}

function StandaloneFunnelLinkButton({ url }: { url: string }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const handleClick = () => {
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      toast({ title: t('products.standaloneLinkCopied') });
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={t('products.copyStandaloneLink')}
          className="mr-1 size-7 flex items-center justify-center rounded-full bg-muted text-muted-foreground hover:text-foreground active:scale-90 transition-all duration-150 cursor-pointer"
          onClick={handleClick}
        >
          {copied ? <Check className="size-3.5 text-green-500" /> : <Globe className="size-3.5" />}
        </button>
      </TooltipTrigger>
      <TooltipContent>{t('products.copyStandaloneLink')}</TooltipContent>
    </Tooltip>
  );
}

function ProductDocumentsContent({ productId }: { productId: string }) {
  const { t } = useTranslation();

  const { data: commonDocuments, status: configurationStatus } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(productId),
    select: data => [
      ...new Set([
        ...data.commonDocuments.map(({ type }) => type),
        ...data.generatedDocuments
          .filter(d => d.provider === 'Static')
          .map(d => d.documentType ?? d.configName),
      ]),
    ],
  });

  const { data: documents, status: listDocumentsStatus } = useQuery({
    ...viewSpecificControllerListProductCommonDocumentsQueryOptions(productId),
    select: data =>
      data?.data.map(document => ({
        ...document,
        metadata: keyValueFieldsToObject(document.metadata),
      })),
  });

  const groups = (commonDocuments ?? []).map(category => ({
    id: category,
    label: t(`documents.${category}`),
    documents: (documents ?? []).filter(document => document.category === category).slice(0, 1),
  }));
  const displayedDocuments = groups.flatMap(({ documents }) => documents);
  const viewableDocuments = [
    ...new Map(displayedDocuments.map(document => [document.id, document])).values(),
  ].filter(({ signedUrl }) => Boolean(signedUrl));
  const { selectedDocument, selectDocument, closeViewer } = useDocumentViewer(viewableDocuments);
  const hasError = configurationStatus === 'error' || listDocumentsStatus === 'error';
  const isLoading =
    !hasError && (configurationStatus === 'pending' || listDocumentsStatus === 'pending');

  return (
    <>
      {isLoading && (
        <Stack gap={2} className="pt-3">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </Stack>
      )}

      {hasError && (
        <div className="text-center text-muted-foreground py-3 text-sm">
          {t('products.documentsLoadError')}
        </div>
      )}

      {configurationStatus === 'success' &&
        listDocumentsStatus === 'success' &&
        (displayedDocuments.length > 0 ? (
          <DocumentsCollection groups={groups} onView={selectDocument} />
        ) : (
          <div className="text-center text-muted-foreground py-2 text-sm">
            {t('products.noDocumentAvailable')}
          </div>
        ))}

      <DocumentViewer
        productId={productId}
        document={selectedDocument}
        documents={viewableDocuments}
        onClose={closeViewer}
        onDocumentChange={selectDocument}
      />
    </>
  );
}
